/**
 * LlamaParse parser — standard-tier async OCR (LlamaIndex Cloud).
 *
 * The authoring contract is {@link llamaparseDefinition}: a `ParserDefinition`
 * whose `submitDocument`/`pollDocument` slots carry the durable job handle.
 * The legacy `Parser` Promise surface ({@link llamaparseParser}) and the
 * standalone Effect helpers are derived from it; nothing here is a second
 * implementation of the vendor transport.
 */

import { Clock, Effect, Layer, Option, Schema, Stream } from 'effect';

import {
  DocumentSource,
  ParserHttp,
  ParserSecrets,
  bytesRef,
  documentBytesEffect,
  documentSourceBytesOnlyService,
  makeParserDependencyError,
  parserDependencyErrorCause,
  type ParseDocumentRef,
} from '../parser-host';
import {
  defineDocumentParser,
  makeParserError,
  parserErrorCause,
  type ParseDocumentInput,
  type ParseEvent,
  type ParsePageInput,
  type ParsedPage,
  type ParserError,
} from '../parser-layer';
import {
  defineEffectParser,
  type DefinedEffectParser,
  type ParserInput,
  type ParserJobHandle,
  type ParserOutput,
  type ParserPollResult,
  type ParserSpec,
} from '../parser';
import type { BlockType, CanonicalBlock, CanonicalPage, CanonicalParseResult } from '../types';
import { normalizePixelBbox } from '../bbox';

const SPEC: ParserSpec = {
  id: 'llamaparse',
  name: 'LlamaParse',
  version: '1.0.0',
  tier: 'standard',
  category: 'ocr',
  requiredEnvKeys: ['LLAMAINDEX_API_KEY'],
  inputs: ['pdf-bytes'],
  outputs: ['parsed-result'],
  properties: [
    {
      name: 'timeoutMs',
      displayName: 'Timeout (ms)',
      type: 'number',
      default: 120_000,
      description: 'Max polling timeout for job completion',
    },
    {
      name: 'baseUrl',
      displayName: 'API Base URL',
      type: 'string',
      default: 'https://api.cloud.llamaindex.ai/api/parsing',
    },
  ],
  bestFor: ['structured documents', 'tables', 'headings'],
  async: true,
  pollStrategy: { initialDelayMs: 5_000, maxDelayMs: 60_000, backoffMultiplier: 2 },
};

const DEFAULT_BASE_URL = SPEC.properties[1].default as string;
const DEFAULT_TIMEOUT_MS = SPEC.properties[0].default as number;
const DEFAULT_PARSE_MODE = 'parse_page_with_llm';
const POLL_INTERVAL_MS = 2_000;
const MAX_ERROR_DETAIL_LENGTH = 1_000;

const UploadResponse = Schema.Struct({
  id: Schema.NonEmptyString,
});

const LlamaParseItem = Schema.StructWithRest(
  Schema.Struct({
    type: Schema.String,
    value: Schema.optionalKey(Schema.String),
    md: Schema.optionalKey(Schema.String),
    bBox: Schema.optionalKey(
      Schema.Struct({
        x: Schema.Number,
        y: Schema.Number,
        w: Schema.Number,
        h: Schema.Number,
      }),
    ),
    rows: Schema.optionalKey(Schema.Array(Schema.Array(Schema.String))),
  }),
  [Schema.Record(Schema.String, Schema.Unknown)],
);

const LlamaParseResponse = Schema.Struct({
  pages: Schema.optionalKey(
    Schema.Array(
      Schema.StructWithRest(
        Schema.Struct({
          page: Schema.Int,
          width: Schema.optionalKey(Schema.Number),
          height: Schema.optionalKey(Schema.Number),
          items: Schema.optionalKey(Schema.Array(LlamaParseItem)),
        }),
        [Schema.Record(Schema.String, Schema.Unknown)],
      ),
    ),
  ),
});

type LlamaParseRaw = typeof LlamaParseResponse.Type;

export type LlamaParseOperation =
  | 'input'
  | 'options'
  | 'multipart'
  | 'upload'
  | 'upload-decode'
  | 'upload_transport'
  | 'upload_status'
  | 'upload_decode'
  | 'poll'
  | 'poll-decode'
  | 'poll_transport'
  | 'poll_status'
  | 'poll_decode'
  | 'timeout'
  | 'projection';

/**
 * Redacted LlamaParse failure carried as the private cause of a `ParserError`.
 * The serializable `cause` field is never populated; original causes live in a
 * WeakMap and are restored only at Promise compatibility edges.
 */
export class LlamaParseError extends Schema.TaggedError<LlamaParseError>()('LlamaParseError', {
  operation: Schema.Literals([
    'input',
    'options',
    'multipart',
    'upload',
    'upload-decode',
    'upload_transport',
    'upload_status',
    'upload_decode',
    'poll',
    'poll-decode',
    'poll_transport',
    'poll_status',
    'poll_decode',
    'timeout',
    'projection',
  ]),
  message: Schema.String,
  status: Schema.optionalKey(Schema.Int),
  cause: Schema.optionalKey(Schema.Defect()),
}) {}

const llamaParseErrorCauses = new WeakMap<LlamaParseError, unknown>();

function llamaFailure(
  operation: LlamaParseOperation,
  message: string,
  cause?: unknown,
  status?: number,
): LlamaParseError {
  const error = new LlamaParseError({
    operation,
    message,
    ...(status === undefined ? {} : { status }),
  });
  if (cause !== undefined) llamaParseErrorCauses.set(error, cause);
  return error;
}

/** Recover the private cause chain beneath a LlamaParse failure. */
export function llamaParseErrorCause(error: unknown): unknown {
  return error instanceof LlamaParseError
    ? (llamaParseErrorCauses.get(error) ?? error)
    : parserDependencyErrorCause(error);
}

/** LlamaParse type → canonical BlockType (used at export/render boundary). */
const LLAMAPARSE_TYPE_MAP: Record<string, BlockType> = {
  table: 'table',
  row: 'row',
  cell: 'cell',
  text: 'text',
  paragraph: 'text',
  figure: 'figure',
  image: 'figure',
  header: 'header',
  title: 'header',
  heading: 'header',
  footer: 'footer',
  key_value: 'key_value',
  list: 'list',
};

function resolveLlamaParseType(rawType: string): BlockType | undefined {
  return LLAMAPARSE_TYPE_MAP[rawType.toLowerCase()];
}

function toParserError(page?: number) {
  return (error: LlamaParseError): ParserError =>
    makeParserError(
      {
        parserId: 'llamaparse',
        ...(page === undefined ? {} : { page }),
        code: error.operation,
        retryable: error.operation === 'upload_transport' || error.operation === 'poll_transport',
        message: error.message,
      },
      error,
    );
}

interface LlamaParseConfiguration {
  readonly apiKey: string;
  readonly baseUrl: string;
  readonly timeoutMs: number;
}

function baseUrlFromOptions(options: Readonly<Record<string, unknown>> | undefined): string {
  const baseUrl = typeof options?.baseUrl === 'string' ? options.baseUrl.trim() : '';
  return baseUrl || DEFAULT_BASE_URL;
}

function timeoutFromOptions(options: Readonly<Record<string, unknown>> | undefined): number {
  const timeoutMs = options?.timeoutMs;
  // Negative values are meaningful: they force an immediate timeout (pinned behavior).
  return typeof timeoutMs === 'number' && Number.isFinite(timeoutMs)
    ? timeoutMs
    : DEFAULT_TIMEOUT_MS;
}

const requireApiKey = Effect.fn('okrapdf/parser-sdk.llamaparse.requireApiKey')(function* () {
  const secrets = yield* ParserSecrets;
  const apiKey = Option.getOrUndefined(yield* secrets.get('LLAMAINDEX_API_KEY'))?.trim() ?? '';
  return apiKey ? apiKey : yield* llamaFailure('input', 'LlamaParse requires LLAMAINDEX_API_KEY');
});

function serializeVendorOption(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

/**
 * Provider-options passthrough in the AI SDK style: forward arbitrary vendor
 * request fields while reserving transport-only keys for the parser itself.
 */
function buildLlamaParseParams(
  options?: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const params: Record<string, unknown> = { ...(options ?? {}) };
  if (params.result_type == null) params.result_type = 'json';
  if (params.parse_mode == null) params.parse_mode = DEFAULT_PARSE_MODE;

  if (typeof params.parse_mode !== 'string' || params.parse_mode.trim().length === 0) {
    throw new Error(`Unsupported LlamaParse parse_mode: ${String(params.parse_mode)}`);
  }

  // Transport-only or deprecated credentials/options must never be forwarded.
  delete params.baseUrl;
  delete params.timeoutMs;
  delete params.api_key;
  delete params.apiKey;
  delete params.premium_mode;
  delete params.mode;
  return params;
}

function buildLlamaParseFormEffect(
  bytes: Uint8Array,
  params: Record<string, unknown>,
): Effect.Effect<FormData, LlamaParseError> {
  return Effect.try({
    try: () => {
      const formData = new FormData();
      formData.append(
        'file',
        new Blob([bytes.slice().buffer], { type: 'application/pdf' }),
        'document.pdf',
      );
      for (const [key, value] of Object.entries(params)) {
        if (value == null) continue;
        formData.append(key, serializeVendorOption(value));
      }
      return formData;
    },
    catch: (cause) =>
      llamaFailure('multipart', 'Unable to construct the LlamaParse upload.', cause),
  });
}

function responseErrorDetailEffect(response: Response): Effect.Effect<string> {
  return Effect.tryPromise({
    try: () => response.text(),
    catch: () => undefined,
  }).pipe(
    Effect.catch(() => Effect.succeed('')),
    Effect.map((text) => text.replace(/\s+/g, ' ').trim().slice(0, MAX_ERROR_DETAIL_LENGTH)),
  );
}

function documentBytes(
  input: ParseDocumentInput,
): Effect.Effect<Uint8Array, LlamaParseError, DocumentSource> {
  return documentBytesEffect(input.document).pipe(
    Effect.mapError((cause) =>
      llamaFailure('input', 'LlamaParse requires pdfBytes input', llamaParseErrorCause(cause)),
    ),
    Effect.filterOrElse(
      (bytes) => bytes.length > 0,
      () => llamaFailure('input', 'LlamaParse requires pdfBytes input'),
    ),
  );
}

const submitEffect = Effect.fn('okrapdf/parser-sdk.llamaparse.submit')(function* (
  input: ParseDocumentInput,
): Effect.fn.Return<ParserJobHandle, ParserError, ParserHttp | ParserSecrets | DocumentSource> {
  const program: Effect.Effect<
    ParserJobHandle,
    LlamaParseError,
    ParserHttp | ParserSecrets | DocumentSource
  > = Effect.gen(function* () {
      const http = yield* ParserHttp;
      const apiKey = yield* requireApiKey();
      const bytes = yield* documentBytes(input);
      const baseUrl = baseUrlFromOptions(input.options);
      const params = yield* Effect.try({
        try: () => buildLlamaParseParams(input.options),
        catch: (cause) => llamaFailure('options', 'LlamaParse options are invalid.', cause),
      });
      const formData = yield* buildLlamaParseFormEffect(bytes, params);
      const response = yield* http
        .fetch(`${baseUrl}/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${apiKey}` },
          body: formData,
        })
        .pipe(
          Effect.mapError((cause) =>
            llamaFailure(
              'upload_transport',
              'LlamaParse upload request failed.',
              llamaParseErrorCause(cause),
            ),
          ),
        );
      if (!response.ok) {
        const detail = yield* responseErrorDetailEffect(response);
        const cause = new Error(
          `LlamaParse upload failed: ${response.status}${detail ? `: ${detail}` : ''}`,
        );
        return yield* llamaFailure(
          'upload_status',
          'LlamaParse upload returned an unsuccessful response.',
          cause,
          response.status,
        );
      }
      const body = yield* Effect.tryPromise({
        try: () => response.text(),
        catch: (cause) =>
          llamaFailure('upload_decode', 'Unable to decode the LlamaParse upload response.', cause),
      });
      const upload = yield* Schema.decodeUnknownEffect(Schema.fromJsonString(UploadResponse))(
        body,
      ).pipe(
        Effect.mapError((cause) =>
          llamaFailure(
            'upload_decode',
            'LlamaParse upload returned an invalid response',
            cause,
            response.status,
          ),
        ),
      );
      const submittedAt = yield* Clock.currentTimeMillis;
      return {
        jobId: upload.id,
        parserId: 'llamaparse',
        submittedAt,
        metadata: { baseUrl, params },
      } satisfies ParserJobHandle;
    });
  return yield* program.pipe(Effect.mapError(toParserError()));
});

type LlamaParseBbox = { x: number; y: number; w: number; h: number };

type LlamaParseItemShape = {
  type: string;
  value?: string;
  md?: string;
  bBox?: LlamaParseBbox;
  bbox?: LlamaParseBbox | LlamaParseBbox[];
  rows?: readonly (readonly string[])[];
};

function isFiniteBbox(value: unknown): value is LlamaParseBbox {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const bbox = value as Record<string, unknown>;
  return ['x', 'y', 'w', 'h'].every(
    (key) => typeof bbox[key] === 'number' && Number.isFinite(bbox[key]),
  );
}

function unionBboxes(boxes: LlamaParseBbox[]): LlamaParseBbox | undefined {
  if (boxes.length === 0) return undefined;
  const minX = Math.min(...boxes.map((box) => box.x));
  const minY = Math.min(...boxes.map((box) => box.y));
  const maxX = Math.max(...boxes.map((box) => box.x + box.w));
  const maxY = Math.max(...boxes.map((box) => box.y + box.h));
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

function readItemBbox(item: LlamaParseItemShape): LlamaParseBbox | undefined {
  if (isFiniteBbox(item.bBox)) return item.bBox;
  if (Array.isArray(item.bbox)) return unionBboxes(item.bbox.filter(isFiniteBbox));
  if (isFiniteBbox(item.bbox)) return item.bbox;
  return undefined;
}

function normalizeLlamaParseBbox(
  raw: LlamaParseBbox | undefined,
  pageW?: number,
  pageH?: number,
): CanonicalBlock['bbox'] {
  if (!raw || raw.w <= 0 || raw.h <= 0) return undefined;

  const pageWidth = typeof pageW === 'number' && Number.isFinite(pageW) && pageW > 1 ? pageW : null;
  const pageHeight =
    typeof pageH === 'number' && Number.isFinite(pageH) && pageH > 1 ? pageH : null;
  const alreadyNormalized =
    raw.x >= 0 && raw.y >= 0 && raw.x + raw.w <= 1.0001 && raw.y + raw.h <= 1.0001;

  if (alreadyNormalized) return raw;
  if (!pageWidth || !pageHeight) {
    // Fall back to the point-space normalizer when page geometry is unknown.
    return normalizePixelBbox(raw, pageW, pageH);
  }

  return {
    x: raw.x / pageWidth,
    y: raw.y / pageHeight,
    w: raw.w / pageWidth,
    h: raw.h / pageHeight,
  };
}

function normalizeItem(item: LlamaParseItemShape, pageW?: number, pageH?: number): CanonicalBlock {
  const bbox = normalizeLlamaParseBbox(readItemBbox(item), pageW, pageH);

  if (item.type === 'table' && item.rows) {
    return {
      type: 'table',
      label: 'Table',
      ...(bbox === undefined ? {} : { bbox }),
      children: item.rows.map((row, rowIndex) => ({
        type: 'row',
        label: `Row ${rowIndex}`,
        children: row.map((cellValue) => ({
          type: 'cell',
          value: cellValue,
        })),
      })),
    };
  }

  const value = item.value || item.md || undefined;
  const label = item.type === 'heading' ? item.value || item.md : undefined;
  return {
    type: item.type,
    ...(label === undefined ? {} : { label }),
    ...(value === undefined ? {} : { value }),
    ...(bbox === undefined ? {} : { bbox }),
  };
}

function normalizeResult(
  data: LlamaParseRaw,
  durationMs: number,
  debug?: Record<string, unknown>,
): ParserOutput {
  const pages: CanonicalPage[] = (data.pages ?? []).map((page) => ({
    pageNumber: page.page,
    width: page.width,
    height: page.height,
    blocks: (page.items ?? []).map((item) => normalizeItem(item, page.width, page.height)),
  }));

  return {
    parsedResult: {
      pages,
      metadata: {
        vendor: 'LlamaParse',
        durationMs,
        confidence: null,
        pageCount: pages.length,
      },
    },
    rawPages: (data.pages ?? []).map((page) => ({ pageNumber: page.page, raw: page })),
    durationMs,
    debug,
  };
}

const pollOnceEffect = Effect.fn('okrapdf/parser-sdk.llamaparse.poll')(function* (
  handle: ParserJobHandle,
): Effect.fn.Return<ParserPollResult, ParserError, ParserHttp | ParserSecrets | DocumentSource> {
  const program: Effect.Effect<
    ParserPollResult,
    LlamaParseError,
    ParserHttp | ParserSecrets | DocumentSource
  > = Effect.gen(function* () {
      const http = yield* ParserHttp;
      const apiKey = yield* requireApiKey();
      const baseUrl =
        typeof handle.metadata?.baseUrl === 'string' && handle.metadata.baseUrl.trim()
          ? handle.metadata.baseUrl.trim()
          : DEFAULT_BASE_URL;
      const response = yield* http
        .fetch(`${baseUrl}/job/${encodeURIComponent(handle.jobId)}/result/json`, {
          headers: { Authorization: `Bearer ${apiKey}` },
        })
        .pipe(
          Effect.mapError((cause) =>
            llamaFailure(
              'poll_transport',
              'LlamaParse poll request failed.',
              llamaParseErrorCause(cause),
            ),
          ),
        );

      if (response.status === 404 || response.status === 202) {
        return { done: false } satisfies ParserPollResult;
      }
      if (response.status !== 200) {
        const detail = yield* responseErrorDetailEffect(response);
        const cause = new Error(
          `LlamaParse poll failed: ${response.status}${detail ? `: ${detail}` : ''}`,
        );
        return yield* llamaFailure(
          'poll_status',
          'LlamaParse poll returned an unsuccessful response.',
          cause,
          response.status,
        );
      }

      const body = yield* Effect.tryPromise({
        try: () => response.text(),
        catch: (cause) =>
          llamaFailure('poll_decode', 'Unable to decode the LlamaParse poll response.', cause),
      });
      const result = yield* Schema.decodeUnknownEffect(Schema.fromJsonString(LlamaParseResponse))(
        body,
      ).pipe(
        Effect.mapError((cause) =>
          llamaFailure(
            'poll_decode',
            'LlamaParse poll returned an invalid parse result',
            cause,
            response.status,
          ),
        ),
      );
      const now = yield* Clock.currentTimeMillis;
      return {
        done: true,
        output: normalizeResult(result, now - handle.submittedAt, {
          jobId: handle.jobId,
          baseUrl,
          params: handle.metadata?.params,
        }),
      } satisfies ParserPollResult;
    });
  return yield* program.pipe(Effect.mapError(toParserError()));
});

/**
 * Submit, poll to completion with the established 2 s interval and `timeoutMs`
 * deadline, and return the vendor output. Shared by the definition's
 * `parseDocument`/`parsePage` slots and the derived server helpers.
 */
export const runLlamaParseDocumentEffect = Effect.fn('okrapdf/parser-sdk.llamaparse.runDocument')(
  function* (
  input: ParseDocumentInput,
  ): Effect.fn.Return<ParserOutput, ParserError, ParserHttp | ParserSecrets | DocumentSource> {
  const timeoutMs = timeoutFromOptions(input.options);
  const handle = yield* submitEffect(input);
  const deadline = (yield* Clock.currentTimeMillis) + timeoutMs;

  while (true) {
    if ((yield* Clock.currentTimeMillis) >= deadline) {
      const cause = new Error('LlamaParse timed out');
      return yield* makeParserError(
        {
          parserId: 'llamaparse',
          code: 'timeout',
          retryable: false,
          message: 'LlamaParse polling timed out.',
        },
        llamaFailure('timeout', 'LlamaParse polling timed out.', cause),
      );
    }
    const result = yield* pollOnceEffect(handle);
    if (result.done && result.output) return result.output;
    yield* Effect.sleep(POLL_INTERVAL_MS);
  }
  },
);

function pageEvents(output: ParserOutput): ParseEvent[] {
  const parsed = output.parsedResult;
  if (!parsed) return [];
  return parsed.pages.map((page, index) => ({
    type: 'page' as const,
    page: {
      page: page.pageNumber,
      blocks: page.blocks,
      ...(page.width === undefined ? {} : { width: page.width }),
      ...(page.height === undefined ? {} : { height: page.height }),
      raw: output.rawPages?.[index]?.raw,
    } satisfies ParsedPage,
  }));
}

/** The LlamaParse authoring contract: one definition, every surface derives. */
export const llamaparseDefinition = defineDocumentParser({
  spec: {
    ...SPEC,
    provides: ['text', 'layout', 'tables', 'bbox'],
    network: { allowedHosts: ['api.cloud.llamaindex.ai'] },
  },
  available: Effect.gen(function* () {
    const secrets = yield* ParserSecrets;
    return Option.isSome(yield* secrets.get('LLAMAINDEX_API_KEY'));
  }),
  parsePage: (input: ParsePageInput) =>
    Effect.gen(function* () {
      const output = yield* runLlamaParseDocumentEffect({
        document: input.document,
        ...(input.options === undefined ? {} : { options: input.options }),
      });
      const page = output.parsedResult?.pages.find(
        (candidate) => candidate.pageNumber === input.page,
      );
      if (!page) {
        return yield* makeParserError({
          parserId: 'llamaparse',
          page: input.page,
          code: 'empty_output',
          retryable: false,
          message: `llamaparse returned no blocks for page ${input.page}`,
        });
      }
      const rawIndex = output.parsedResult!.pages.indexOf(page);
      return {
        page: input.page,
        blocks: page.blocks,
        ...(page.width === undefined ? {} : { width: page.width }),
        ...(page.height === undefined ? {} : { height: page.height }),
        raw: output.rawPages?.[rawIndex]?.raw,
      } satisfies ParsedPage;
    }),
  parseDocument: (input: ParseDocumentInput) =>
    Stream.unwrap(
      Effect.map(runLlamaParseDocumentEffect(input), (output) =>
        Stream.fromIterable(pageEvents(output)),
      ),
    ),
  submitDocument: submitEffect,
  pollDocument: pollOnceEffect,
  detect(data: unknown): boolean {
    if (!data || typeof data !== 'object') return false;
    const obj = data as Record<string, unknown>;
    if (!Array.isArray(obj.pages)) return false;
    const first = (obj.pages as unknown[])[0];
    if (!first || typeof first !== 'object') return false;
    const page = first as Record<string, unknown>;
    if (!Array.isArray(page.items)) return false;
    const firstItem = (page.items as unknown[])[0];
    if (!firstItem || typeof firstItem !== 'object') return false;
    return (
      'bBox' in (firstItem as Record<string, unknown>) ||
      'bbox' in (firstItem as Record<string, unknown>)
    );
  },
  normalize: (data: unknown) =>
    Effect.succeed({
      pages: ((data as LlamaParseRaw).pages ?? []).map(
        (page): CanonicalPage => ({
          pageNumber: page.page,
          width: page.width,
          height: page.height,
          blocks: (page.items ?? []).map((item) => normalizeItem(item, page.width, page.height)),
        }),
      ),
      metadata: {
        vendor: 'external:llamaparse',
        durationMs: 0,
        confidence: 0.8,
        pageCount: ((data as LlamaParseRaw).pages ?? []).length,
      },
    } satisfies CanonicalParseResult),
  resolveType: resolveLlamaParseType,
});

/** Host for the derived legacy Promise surface: global fetch + per-call env secrets. */
function legacyHost(
  env: Record<string, unknown>,
): Layer.Layer<ParserHttp | ParserSecrets | DocumentSource> {
  return Layer.mergeAll(
    Layer.succeed(
      ParserHttp,
      ParserHttp.of({
        fetch: (input, init) =>
          Effect.tryPromise({
            try: () => globalThis.fetch(input, init),
            catch: (cause) =>
              makeParserDependencyError(
                { service: 'http', operation: 'fetch', code: 'request_failed', retryable: true },
                cause,
              ),
          }),
      }),
    ),
    Layer.succeed(
      ParserSecrets,
      ParserSecrets.of({
        get: (name) =>
          Effect.succeed(
            Option.fromNullishOr(typeof env[name] === 'string' ? (env[name] as string) : undefined),
          ),
      }),
    ),
    Layer.succeed(DocumentSource, documentSourceBytesOnlyService),
  );
}

function legacyDocumentRef(input: ParserInput): ParseDocumentRef {
  return bytesRef({
    documentId: input.context?.documentId ?? 'legacy:llamaparse',
    bytes: new Uint8Array(input.pdfBytes!),
    ...(input.context?.fileName === undefined ? {} : { fileName: input.context.fileName }),
    ...((input.totalPages ?? input.context?.pageCount) === undefined
      ? {}
      : { pageCount: input.totalPages ?? input.context?.pageCount }),
  });
}

function legacyMissingPdf(): LlamaParseError {
  return llamaFailure('input', 'LlamaParse requires pdfBytes input');
}

/** Whole-document program derived from the definition (kept for legacy callers). */
export function parseLlamaParse(
  input: ParserInput,
  env: Record<string, unknown>,
): Effect.Effect<ParserOutput, unknown> {
  if (!input.pdfBytes) return Effect.fail(legacyMissingPdf());
  return runLlamaParseDocumentEffect({
    document: legacyDocumentRef(input),
    ...(input.parameters === undefined ? {} : { options: input.parameters }),
  }).pipe(Effect.provide(legacyHost(env)), Effect.mapError(parserErrorCause));
}

/** Standalone submit derived from the definition (kept for legacy callers). */
export function submitLlamaParse(
  input: ParserInput,
  env: Record<string, unknown>,
): Effect.Effect<ParserJobHandle, unknown> {
  if (!input.pdfBytes) return Effect.fail(legacyMissingPdf());
  return submitEffect({
    document: legacyDocumentRef(input),
    ...(input.parameters === undefined ? {} : { options: input.parameters }),
  }).pipe(Effect.provide(legacyHost(env)), Effect.mapError(parserErrorCause));
}

/** Standalone poll derived from the definition (kept for legacy callers). */
export function pollLlamaParse(
  handle: ParserJobHandle,
  env: Record<string, unknown>,
): Effect.Effect<ParserPollResult, unknown> {
  return pollOnceEffect(handle).pipe(
    Effect.provide(legacyHost(env)),
    Effect.mapError(parserErrorCause),
  );
}

/** @deprecated Author new integrations against {@link llamaparseDefinition}. */
export const llamaparseParser: DefinedEffectParser<unknown> = defineEffectParser({
  spec: SPEC,
  resolveType: resolveLlamaParseType,
  validate(env: Record<string, unknown>): boolean {
    return typeof env.LLAMAINDEX_API_KEY === 'string' && env.LLAMAINDEX_API_KEY.trim().length > 0;
  },
  parseEffect: parseLlamaParse,
  submitEffect: submitLlamaParse,
  pollEffect: pollLlamaParse,
});
